import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Trash, CircleNotch, FolderOpen, Star, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { pillarStyle } from "@/lib/planiq";
import ThemeToggle from "@/components/ThemeToggle";
import Logomark from "@/components/Logomark";
import UserMenu from "@/components/UserMenu";
import { useAuth } from "@/lib/auth";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const KICKER = "text-xs tracking-[0.25em] uppercase font-bold text-gray-500";

const STATUS_BADGE = {
  ready: { label: "Ready", cls: "text-emerald-800 border-emerald-700 bg-emerald-50" },
  generating: { label: "Building…", cls: "text-amber-800 border-amber-600 bg-amber-50" },
  analyzing: { label: "Analyzing…", cls: "text-amber-800 border-amber-600 bg-amber-50" },
  blueprint_ready: { label: "Blueprint", cls: "text-blue-800 border-blue-700 bg-blue-50" },
  error: { label: "Failed", cls: "text-red-700 border-red-600 bg-red-50" },
};

function describe(p) {
  const s = (p.executive_summary || p.raw_context || "").trim();
  if (s) return s.length > 150 ? s.slice(0, 150) + "…" : s;
  if (p.pillars?.length) return p.pillars.map((x) => x.name).join(" · ");
  return "Awaiting generation.";
}

export default function Library() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = () => api.listPlans().then(setPlans).catch(() => setPlans([]));
  useEffect(() => { load(); }, []);

  const confirmPlan = confirmId ? (plans || []).find((p) => p._id === confirmId) : null;

  const doDelete = async () => {
    const id = confirmId;
    setConfirmId(null);
    if (!id) return;
    setDeleting(id);
    try {
      await api.deletePlan(id);
      setPlans((prev) => prev.filter((p) => p._id !== id));
      toast.success("Plan deleted.");
    } catch (err) {
      toast.error("Could not delete plan.");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-gray-200 px-8 py-5 flex items-center justify-between">
        <div className="flex items-baseline gap-3">
          <button onClick={() => navigate(user ? "/library" : "/")} className="flex items-center gap-2">
            <Logomark size={24} />
            <span className="font-display font-black text-2xl tracking-tight">Xartis</span>
          </button>
          <span className={KICKER}>Plan Library</span>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <UserMenu />
          <button onClick={() => navigate("/start")} data-testid="new-plan-button"
            className="inline-flex items-center gap-2 rounded-none bg-gray-950 hover:bg-gray-700 text-white px-5 py-2.5 text-sm font-semibold transition-colors">
            <Plus size={18} weight="bold" /> New Plan
          </button>
        </div>
      </header>

      <main className="flex-1 w-full max-w-6xl mx-auto px-8 py-12">
        <p className={KICKER}>Your initiatives</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none mt-2 mb-10">
          Plan Library
        </h1>

        {plans === null ? (
          <div className="flex items-center gap-3 text-gray-500 font-mono"><CircleNotch size={20} className="animate-spin" /> Loading…</div>
        ) : plans.length === 0 ? (
          <div className="border border-dashed border-gray-300 py-20 flex flex-col items-center text-center" data-testid="library-empty">
            <FolderOpen size={40} weight="duotone" className="text-gray-300 mb-4" />
            <p className="text-gray-500 mb-5">No plans yet. Build your first one.</p>
            <button onClick={() => navigate("/start")} className="inline-flex items-center gap-2 rounded-none bg-gray-950 hover:bg-gray-700 text-white px-5 py-2.5 text-sm font-semibold">
              <Plus size={18} weight="bold" /> Create a Plan
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map((p, i) => {
              const badge = STATUS_BADGE[p.status] || STATUS_BADGE.ready;
              const total = p.total_steps || 0;
              const done = p.complete_steps || 0;
              const pct = total ? (done / total) * 100 : 0;
              return (
                <motion.div
                  key={p._id}
                  data-testid={`plan-tile-${p._id}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: Math.min(i * 0.03, 0.3) }}
                  onClick={() => navigate(`/plan/${p._id}`)}
                  className="group border border-gray-200 bg-white p-5 cursor-pointer hover:border-gray-900 transition-colors flex flex-col"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${badge.cls}`}>{badge.label}</span>
                    <div className="flex items-center gap-2">
                      {p.saved && <Star size={15} weight="fill" className="text-amber-500" />}
                      <button onClick={(e) => { e.stopPropagation(); setConfirmId(p._id); }} data-testid={`delete-plan-${p._id}`}
                        className="text-gray-300 hover:text-red-600 transition-colors" aria-label="Delete plan">
                        {deleting === p._id ? <CircleNotch size={16} className="animate-spin" /> : <Trash size={16} />}
                      </button>
                    </div>
                  </div>

                  <h2 className="font-display font-bold text-lg tracking-tight leading-tight mb-2 group-hover:underline">{p.title}</h2>
                  <p className="text-sm text-gray-500 leading-relaxed flex-1 mb-4">{describe(p)}</p>

                  {/* pillar swatches */}
                  {p.pillars?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {p.pillars.slice(0, 6).map((pl) => (
                        <span key={pl.id} className="w-2.5 h-2.5" style={{ backgroundColor: pillarStyle(pl).hex }} title={pl.name} />
                      ))}
                    </div>
                  )}

                  <div className="mt-auto pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between text-xs text-gray-500 font-mono mb-1.5">
                      <span>{new Date(p.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span>
                      <span className="flex items-center gap-1" data-testid={`plan-subtask-count-${p._id}`}>
                        <CheckCircle size={13} weight={done === total && total > 0 ? "fill" : "regular"} />
                        {done}/{total} sub-tasks · {Math.round(pct)}%
                      </span>
                    </div>
                    <div className="h-1 bg-gray-100">
                      <div className="h-full bg-gray-950 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent className="rounded-none" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this plan?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmPlan ? `"${confirmPlan.title}" and all its action items will be permanently removed. This cannot be undone.` : "This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button" className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="delete-confirm-button"
              className="rounded-none bg-red-600 hover:bg-red-700 text-white">
              Delete plan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
